<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { caregiversApi, type Caregiver } from '@/api/caregivers';
import { patientsApi, type Patient } from '@/api/patients';
import {
  shiftsApi, type Shift, type InstanceShifts,
  type PdfShiftsCalendar, type XlsxShiftsCalendar,
} from '@/api/shifts';
import { type CalendarDateFilter } from '@/api/bulk-exports';
import type { PatientScheduleBlock, ScheduleBlockPrefill } from '@/api/patient-schedule-blocks';
import { useBulkExport } from '@/composables/useBulkExport';
import { useFileDownload } from '@/composables/useFileDownload';
import { useFilters } from '@/composables/useFilters';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/vue-query';
import { useShiftsStore } from '@/stores/shifts';
import {
  convertOffsetBetweenViews, offsetContainingDate, periodStartDate,
} from '@/utils/calendar-offset';
import { useGroupFilterStore } from '@/stores/group-filter';
import { storeToRefs } from 'pinia';
import { usePermissions } from '@/composables/usePermissions';
import { useUser } from '@/composables/useUser';
import { useAccessibleCareers } from '@/composables/useAccessibleCareers';
import { addDays, todayIso } from '@/utils/calendar-events';
import { blockPrefillForSlot } from '@/utils/schedule-blocks';
import { buildCellColumns, rowsHaveShifts } from '@/utils/shifts-calendar-grid';
import BulkExportSnackbars from '@/components/bulk-export-snackbars.vue';
import ResponsiveDialog from '@/components/responsive-dialog.vue';
import ShiftsCalendarFilters from './shifts-calendar-filters.vue';
import ShiftsCalendarGrid from './shifts-calendar-grid.vue';
import ShiftsCalendarToolbar from './shifts-calendar-toolbar.vue';
import ShiftsListSummary from './shifts-list-summary.vue';
import ShiftShowDialog from './shift-show-dialog.vue';
import ShiftForm from './actions/shift-form.vue';
import ShiftBatchDestroyForm from './shift-batch-destroy-form.vue';
import ShiftBatchUpdateForm from './shift-batch-update-form.vue';
import PatientScheduleBlockForm from './schedule-blocks/patient-schedule-block-form.vue';

interface Props {
  patientId: number,
  fromForm: boolean,
}

const props = defineProps<Props>();

const { t } = useI18n();
const { downloadFile } = useFileDownload();

const store = useShiftsStore();

const { hasPermission } = usePermissions();
const { user } = useUser();
const { canAccessCareer } = useAccessibleCareers();

const carePlanningEnabled = computed(() => !!user?.value?.companyCarePlanningEnabled);

// Owned by the parent (shifts-page) so its mobile "Filtros" button can open the
// drawer and read the active-filter badge count.
const drawer = defineModel<boolean>('drawer', { default: false });
const activeFilterCountModel = defineModel<number>('activeFilterCount', { default: 0 });
// Tells the parent when the calendar is on screen (not the initial skeleton),
// so the view toggle only appears alongside actual content.
const contentReadyModel = defineModel<boolean>('contentReady', { default: false });

function initialPatientId() {
  if (props.fromForm) return props.patientId;

  store.patientId = store.patientId ||
    (store.caregiverId ? undefined : props.patientId) || undefined;

  return store.patientId;
}

const currentStartDateTimeFilter = ref<string>(store.startDateTimeFilter || 'weekly');
const currentPatientId = ref<number | undefined>(initialPatientId());
const currentCaregiverId = ref<number | undefined>(store.caregiverId || undefined);
const currentOffset = ref<number>(store.offset || 0);
const currentGroupByClass = ref<'patient' | 'caregiver' | undefined>(store?.groupByClass || 'caregiver');
// Reconcile any persisted career filter on load: the Especialidad control is
// hidden while a professional is selected, and it must never carry a career the
// user can no longer read ("without_career" has no read_ permission but is
// always allowed). Sync the store so filters/store start equal (no spurious refetch).
store.caregiverCareerIn = currentCaregiverId.value ?
  [] :
  (store.caregiverCareerIn || []).filter(
    (value) => value === 'without_career' || canAccessCareer(value),
  );
const currentCaregiverCareerIn = ref<string[]>(store.caregiverCareerIn);
const { groupIds: groupFilterIds } = storeToRefs(useGroupFilterStore());
const currentPatientGroupsIdIn = ref<number[]>(groupFilterIds.value || []);

const filters = ref<{
  patientIdEq: undefined | number,
  caregiverIdEq: undefined | number,
  startDateTimeFilter: undefined | string,
  offset: number,
  groupByClass: 'patient' | 'caregiver' | undefined,
  caregiverCareerIn: string[],
  patientGroupsIdIn: number[],
  priceFilter: string,
  costFilter: string,
  markedStatusFilter: string,
}>({
  patientIdEq: currentPatientId.value,
  caregiverIdEq: currentCaregiverId.value,
  startDateTimeFilter: currentStartDateTimeFilter.value,
  offset: currentOffset.value,
  groupByClass: currentGroupByClass.value,
  caregiverCareerIn: currentCaregiverCareerIn.value,
  patientGroupsIdIn: currentPatientGroupsIdIn.value,
  priceFilter: '',
  costFilter: '',
  markedStatusFilter: '',
});
const queryParams = useFilters(filters.value);
// The query key only moves when a filter change is "committed" (see
// commitQueryParams), so each committed month/filter combination gets its own
// cache entry and navigating back to one is instant.
const committedQueryParams = ref(queryParams.value);

// Badge on the mobile "Filtros" button: how many row filters are active
// (the Vista / Visión General view selectors are not row filters).
const activeFilterCount = computed(() => [
  filters.value.patientIdEq,
  filters.value.caregiverIdEq,
  filters.value.caregiverCareerIn.length > 0 || undefined,
  filters.value.patientGroupsIdIn.length > 0 || undefined,
  filters.value.priceFilter || undefined,
  filters.value.costFilter || undefined,
  filters.value.markedStatusFilter || undefined,
].filter(Boolean).length);

// Surface the count to the parent's mobile filter button badge.
watch(activeFilterCount, (value) => {
  activeFilterCountModel.value = value;
}, { immediate: true });

function updateOffset(offset: number) {
  filters.value.offset += offset;
}

const gridRef = ref<InstanceType<typeof ShiftsCalendarGrid>>();

function resetOffset() {
  filters.value.offset = 0;
  gridRef.value?.scrollToToday();
}

// Toolbar date picker: jump to the period containing the picked date.
const toolbarAnchorDate = computed(
  () => periodStartDate(filters.value.startDateTimeFilter, filters.value.offset),
);

function onToolbarDateSelected(date: string) {
  filters.value.offset = offsetContainingDate(filters.value.startDateTimeFilter, date);
}

const FIVE_MINUTES_IN_MS = 300_000;
const FIFTEEN_MINUTES_IN_MS = 900_000;
const THIRTY_MINUTES_IN_MS = 1_800_000;
const cascadingGroupIds = computed(() => filters.value.patientGroupsIdIn);

const { data: patients, isPending: isPatientsPending } = useQuery({
  queryKey: ['patientsForFilters', cascadingGroupIds],
  queryFn: () => patientsApi.indexForFilters(cascadingGroupIds.value),
  staleTime: FIVE_MINUTES_IN_MS,
  gcTime: THIRTY_MINUTES_IN_MS,
});

const { data: caregivers, isPending: isCaregiversPending } = useQuery({
  queryKey: ['caregiversForFilters', cascadingGroupIds],
  queryFn: () => caregiversApi.indexForFilters(cascadingGroupIds.value),
  staleTime: FIVE_MINUTES_IN_MS,
  gcTime: THIRTY_MINUTES_IN_MS,
});

const queryClient = useQueryClient();

const {
  data: shiftsAsCalendar,
  isPending: isShiftsAsCalendarPending,
  isFetching: isShiftsAsCalendarFetching,
} = useQuery({
  queryKey: ['shiftsAsCalendar', committedQueryParams],
  queryFn: () => shiftsApi.indexAsCalendar(committedQueryParams.value),
  // staleTime stays 0: revisiting a cached month paints it instantly but
  // always revalidates in the background, so changes made by other users or
  // the mobile app (check-ins, new shifts) appear as soon as the server
  // responds — same freshness as the previous refetch-on-every-navigation.
  // gcTime is shorter than the filter queries': large tenants have multi-MB
  // months and cached entries only serve as instant placeholders.
  gcTime: FIFTEEN_MINUTES_IN_MS,
  // Keep showing the current grid while the next month/filter loads instead of
  // tearing the table down to a skeleton.
  placeholderData: keepPreviousData,
  // The payload holds thousands of shift cells; deep-proxying it is a large
  // part of the monthly view's render cost and nothing mutates it.
  shallow: true,
});

// Refetches the visible calendar and marks every other cached month stale so
// mutations (create/update/destroy) never show outdated cached months.
function refetchAsCalendar() {
  queryClient.invalidateQueries({ queryKey: ['shiftsAsCalendar'] });
}

const currentPatient = computed(() => {
  if (!filters.value.patientIdEq) return undefined;

  return patients.value?.find((patient) => patient.id === filters.value.patientIdEq);
});

const currentCaregiver = computed(() => {
  if (!filters.value.caregiverIdEq) return undefined;

  return caregivers.value?.find((caregiver) => caregiver.id === filters.value.caregiverIdEq);
});

function checkIfStoreIsEqualToFilters() {
  const pairs: [unknown, unknown][] = [
    [store.patientId, filters.value.patientIdEq],
    [store.caregiverId, filters.value.caregiverIdEq],
    [store.startDateTimeFilter, filters.value.startDateTimeFilter],
    [store.offset, filters.value.offset],
    [store.groupByClass, filters.value.groupByClass],
    [store.caregiverCareerIn, filters.value.caregiverCareerIn],
    [groupFilterIds.value, filters.value.patientGroupsIdIn],
  ];

  return pairs.every(([a, b]) => a === b);
}

function updateStoreFromFilters() {
  if (store.startDateTimeFilter !== filters.value.startDateTimeFilter) {
    filters.value.offset = convertOffsetBetweenViews(
      store.startDateTimeFilter,
      filters.value.startDateTimeFilter,
      filters.value.offset,
    );
  }
  store.$patch({
    patientId: filters.value.patientIdEq,
    caregiverId: filters.value.caregiverIdEq,
    startDateTimeFilter: filters.value.startDateTimeFilter,
    offset: filters.value.offset,
    groupByClass: filters.value.groupByClass,
    caregiverCareerIn: filters.value.caregiverCareerIn,
  });
  groupFilterIds.value = filters.value.patientGroupsIdIn;
}

watch(groupFilterIds, (value) => {
  if (filters.value.patientGroupsIdIn !== value) {
    filters.value.patientGroupsIdIn = value;
  }
});

const actionSuccess = ref(false);
const shiftShowDialogRef = ref<InstanceType<typeof ShiftShowDialog>>();

const isPatientFilterFocused = ref(false);
const isCaregiverFilterFocused = ref(false);
function isFilterFocusedWithoutSelection() {
  return (isPatientFilterFocused.value || isCaregiverFilterFocused.value) &&
    !filters.value.patientIdEq && !filters.value.caregiverIdEq;
}

function commitQueryParams() {
  if (isFilterFocusedWithoutSelection()) return;

  if (committedQueryParams.value !== queryParams.value) {
    committedQueryParams.value = queryParams.value;
  }
}

function applyAdvancedFilters(payload: {
  priceFilter: string, costFilter: string, markedStatusFilter: string,
}) {
  filters.value.priceFilter = payload.priceFilter;
  filters.value.costFilter = payload.costFilter;
  filters.value.markedStatusFilter = payload.markedStatusFilter;

  // Advanced filters are not mirrored in the store, so the filters watcher's
  // store-equality guard skips them — commit explicitly.
  commitQueryParams();
}

watch(isPatientFilterFocused, () => {
  if (isPatientFilterFocused.value === false) {
    commitQueryParams();
  }
});

watch(isCaregiverFilterFocused, () => {
  if (isCaregiverFilterFocused.value === false) {
    commitQueryParams();
  }
});

watch(filters, () => {
  if (checkIfStoreIsEqualToFilters()) return;

  updateStoreFromFilters();

  commitQueryParams();
}, { deep: true });

watch(patients, () => {
  if (filters.value.patientIdEq && !currentPatient.value) {
    filters.value.patientIdEq = undefined;
  }
});

watch(caregivers, () => {
  if (filters.value.caregiverIdEq && !currentCaregiver.value) {
    filters.value.caregiverIdEq = undefined;
  }
});

const isGeneralViewActive = computed<boolean>((previous) => {
  if (previous === true) {
    return !currentPatient.value && !currentCaregiver.value;
  }

  return !currentPatient.value && !currentCaregiver.value &&
    !isPatientFilterFocused.value && !isCaregiverFilterFocused.value;
});

const currentViewGroupByClass = computed(() => {
  if (currentPatient.value) return 'caregiver';
  if (currentCaregiver.value) return 'patient';
  if (isGeneralViewActive.value) return filters.value.groupByClass;
  if (isPatientFilterFocused.value) return 'caregiver';
  if (isCaregiverFilterFocused.value) return 'patient';

  return filters.value.groupByClass;
});

function shouldUsePreviousTitle(previous: string) {
  return ((previous)?.includes(t('shiftsCalendar.patient')) && isPatientFilterFocused.value) ||
    ((previous)?.includes(t('shiftsCalendar.caregiver')) && isCaregiverFilterFocused.value);
}

const title = computed<string>((previous) => {
  if (currentPatient.value) {
    return t('shiftsCalendar.patientTitle', { name: currentPatient.value.fullName });
  } else if (currentCaregiver.value) {
    return t('shiftsCalendar.professionalTitle', { name: currentCaregiver.value.fullName });
  } else if (previous && shouldUsePreviousTitle(previous)) {
    return previous;
  }

  return t('shiftsCalendar.title');
});

const shiftAsCalendarByPatientNotRefreshedYet = computed(
  () => shiftsAsCalendar.value?.groupByClass === 'patient',
);

const shouldShowAllCaregivers = computed(() =>
  !currentPatient.value || shiftAsCalendarByPatientNotRefreshedYet.value || isShiftsAsCalendarPending.value,
);

const caregiversByCurrentPatient = computed(() => {
  if (shouldShowAllCaregivers.value) return caregivers.value;

  const caregiversIds = shiftsAsCalendar.value?.shifts.map(
    (caregiverShifts) => caregiverShifts.caregiver?.id,
  ) ?? [];

  return caregivers.value?.filter((caregiver) => caregiversIds.includes(caregiver.id));
});

const caregiverWithoutShifts = ref(false);
watch(caregiversByCurrentPatient, () => {
  if (!filters.value.caregiverIdEq || isCaregiversPending.value) return;

  const caregiverInstance = caregiversByCurrentPatient.value?.find(
    (caregiver) => caregiver.id === filters.value.caregiverIdEq,
  );
  if (!caregiverInstance) {
    filters.value.caregiverIdEq = undefined;
    caregiverWithoutShifts.value = true;
  }
});

const patientsByCurrentCaregiver = computed(() => {
  if (!currentCaregiver.value || currentPatient.value) return patients.value;

  const patientsIds = shiftsAsCalendar.value?.shifts.map(
    (patientShifts) => patientShifts.patient?.id,
  ) || [];

  return patients.value?.filter((patient) => patientsIds.includes(patient.id));
});

function getTimesFromSlotName(slotName: string) {
  if (slotName.includes('day')) {
    return { startTime: '08:00', endTime: '20:00' };
  } else if (slotName.includes('night')) {
    return { startTime: '20:00', endTime: '08:00' };
  }

  return {};
}

// eslint-disable-next-line complexity
function generateShiftForForm(
  item: { caregiver: Caregiver, patient: Patient } | undefined,
  slotName: string | undefined,
) {
  let shift: { patientId?: number, caregiverId?: number, startTime?: string, endTime?: string } = {};
  if (currentPatient.value) {
    shift.patientId = currentPatient.value.id;
    if (item) shift.caregiverId = item.caregiver.id;
  } else if (currentCaregiver.value) {
    shift.caregiverId = currentCaregiver.value.id;
    if (item) shift.patientId = item.patient.id;
  } else if (filters.value.groupByClass === 'caregiver') {
    if (item) shift.caregiverId = item.caregiver.id;
  } else if (filters.value.groupByClass === 'patient') {
    if (item) shift.patientId = item.patient.id;
  }

  if (slotName) shift = { ...shift, ...getTimesFromSlotName(slotName) };

  return shift as Shift;
}

const showCreateDialog = ref(false);
const selectedCreateShift = ref<Partial<Shift> | null>(null);
const selectedCreateDate = ref<string | null>(null);
const selectedCreateSource = ref<'calendar' | 'date'>('calendar');

const showBatchUpdateDialog = ref(false);
const showBatchDestroyDialog = ref(false);
const selectedRowCaregiver = ref<Caregiver | null>(null);

function openShiftShowDialog(shiftId: number) {
  shiftShowDialogRef.value?.open(shiftId);
}

function openCreateDialog(
  item: { caregiver: Caregiver, patient: Patient } | undefined,
  slotName: string | undefined,
  date: string,
  source: 'calendar' | 'date' = 'calendar',
) {
  selectedCreateShift.value = generateShiftForForm(item, slotName);
  selectedCreateDate.value = date;
  selectedCreateSource.value = source;
  showCreateDialog.value = true;
}

// Grid events → page-owned dialogs.
function onCreateCell(payload: { row: InstanceShifts, slotName: string | undefined, date: string }) {
  openCreateDialog(
    payload.row as unknown as { caregiver: Caregiver, patient: Patient },
    payload.slotName,
    payload.date,
  );
}

function onCreateDate(date: string) {
  openCreateDialog(undefined, undefined, date, 'date');
}

function onEditShifts(caregiver: Caregiver | null) {
  selectedRowCaregiver.value = caregiver;
  showBatchUpdateDialog.value = true;
}

function onStopShifts(caregiver: Caregiver | null) {
  selectedRowCaregiver.value = caregiver;
  showBatchDestroyDialog.value = true;
}

// Patient schedule blocks ("bloqueos de horario"): created from the toolbar "+" menu
// or from an empty cell of a patient-filtered calendar, edited by clicking the block.
const canOfferScheduleBlock = computed(() => (
  carePlanningEnabled.value && !!currentPatient.value && hasPermission.value('shifts', 'create')
));
const blockRowPatientId = computed(() => (
  carePlanningEnabled.value ? currentPatient.value?.id : undefined
));
const showBlockDialog = ref(false);
const selectedBlock = ref<PatientScheduleBlock | null>(null);
const blockPrefill = ref<ScheduleBlockPrefill | null>(null);

function openBlockDialog(prefill: ScheduleBlockPrefill | null, block: PatientScheduleBlock | null) {
  selectedBlock.value = block;
  blockPrefill.value = prefill;
  showBlockDialog.value = true;
}

const DAY_SLOT_TIMES = { startTime: '08:00', endTime: '20:00' };
const NIGHT_SLOT_TIMES = { startTime: '20:00', endTime: '08:00' };

// "Turno | Bloqueo" toggle in the create dialogs: swaps forms keeping the same
// patient/date/times prefill (an end at or before the start spans to the next day).
function onSwitchToBlock() {
  showCreateDialog.value = false;
  const {
    startTime = DAY_SLOT_TIMES.startTime,
    endTime = DAY_SLOT_TIMES.endTime,
  } = selectedCreateShift.value ?? {};
  openBlockDialog(blockPrefillForSlot({
    patientId: currentPatient.value?.id,
    startDate: selectedCreateDate.value ?? todayIso(),
    startTime,
    endTime,
  }), null);
}

function onSwitchToShift() {
  showBlockDialog.value = false;
  selectedCreateShift.value = {
    patientId: currentPatient.value?.id,
    startTime: blockPrefill.value?.startTime,
    endTime: blockPrefill.value?.endTime,
  };
  selectedCreateDate.value = blockPrefill.value?.startDate ?? todayIso();
  selectedCreateSource.value = 'calendar';
  showCreateDialog.value = true;
}

function onCreateBlockCell({ slotName, date }: { slotName: string | undefined, date: string }) {
  const night = slotName?.includes('night');
  const startDate = date.slice(0, 'YYYY-MM-DD'.length);
  openBlockDialog({
    patientId: currentPatient.value?.id,
    startDate,
    endDate: night ? addDays(startDate, 1) : startDate,
    ...(night ? NIGHT_SLOT_TIMES : DAY_SLOT_TIMES),
  }, null);
}

function onBlockClick(blockId: number) {
  const block = shiftsAsCalendar.value?.scheduleBlocks?.find((candidate) => candidate.id === blockId);
  if (block) openBlockDialog(null, block);
}

const pdfDownloadSnackbarAlert = ref(false);
const excelDownloadSnackbarAlert = ref(false);
const emptyCalendarSnackbarAlert = ref(false);

const skeletonLoaderActive = computed(
  () => isPatientsPending.value || isCaregiversPending.value || isShiftsAsCalendarPending.value,
);

watch(skeletonLoaderActive, (active) => {
  contentReadyModel.value = !active;
}, { immediate: true });

const showSummary = computed(
  () => !isShiftsAsCalendarPending.value &&
  filters.value.startDateTimeFilter === 'monthly' &&
  hasPermission.value('shifts', 'read_financial_information'),
);

function onCaregiverSelected(caregiverId: number | null | undefined) {
  filters.value.caregiverIdEq = caregiverId ?? undefined;
  if (caregiverId) {
    filters.value.caregiverCareerIn = [];
    currentCaregiverCareerIn.value = [];
    store.caregiverCareerIn = [];
  }
}

const deletedShiftsDateRange = computed(() => {
  const headers = shiftsAsCalendar.value?.headers || [];
  if (headers.length === 0) return { startDate: '', endDate: '' };
  const first = headers[0];
  const last = headers[headers.length - 1];
  if (!first || !last) return { startDate: '', endDate: '' };
  return {
    startDate: first.date,
    endDate: last.date,
  };
});

const {
  startExport,
  isExporting: isBulkExporting,
  progressLabel: bulkExportProgressLabel,
  lastResult: bulkExportLastResult,
  lastErrorMessage: bulkExportLastErrorMessage,
} = useBulkExport();

// The backend requires one of the three period filters; the UI defaults to weekly.
function calendarDateFilter(): CalendarDateFilter {
  const value = filters.value.startDateTimeFilter;

  return value === 'today' || value === 'monthly' ? value : 'weekly';
}

function startCalendarBulkExport(includeRequirements: boolean) {
  startExport({
    exportType: 'calendar',
    groupBy: filters.value.groupByClass ?? 'caregiver',
    startDateTimeFilter: calendarDateFilter(),
    offset: filters.value.offset,
    caregiverCareerIn: filters.value.caregiverCareerIn,
    patientGroupsIdIn: filters.value.patientGroupsIdIn,
    priceFilter: filters.value.priceFilter || undefined,
    costFilter: filters.value.costFilter || undefined,
    markedStatusFilter: filters.value.markedStatusFilter || undefined,
    includeRequirements,
  });
}

// Clearing the Vuetify autocomplete writes null, not undefined — treat both as empty.
function personFilterEmpty(value: number | null | undefined) {
  return value === undefined || value === null;
}

// The weekly-plan visits only print on patient-oriented calendars (a patient selected, or
// the general view grouped by patient), the same rule as ShiftsCalendar#patient_oriented?.
const showRequirementsPdf = computed(() => carePlanningEnabled.value &&
  personFilterEmpty(filters.value.caregiverIdEq) &&
  (!personFilterEmpty(filters.value.patientIdEq) || filters.value.groupByClass === 'patient'));

// General view (no specific person selected — same condition the backend uses to
// merge everyone into one grid): download a ZIP with one PDF per caregiver/patient
// via the bulk export flow instead of one useless combined PDF. The grid holds no
// pending visits, so with requirements the server alone decides whether the ZIP is empty.
function gridHasShifts() {
  const calendar = shiftsAsCalendar.value;

  return !!calendar && rowsHaveShifts(calendar.shifts, buildCellColumns(calendar.headers));
}

function downloadShiftsPDF(includeRequirements = false) {
  if (personFilterEmpty(filters.value.patientIdEq) && personFilterEmpty(filters.value.caregiverIdEq)) {
    if (includeRequirements || gridHasShifts()) {
      startCalendarBulkExport(includeRequirements);
    } else {
      emptyCalendarSnackbarAlert.value = true;
    }

    return;
  }

  pdfDownloadSnackbarAlert.value = true;
  shiftsApi.indexAsCalendarPdf(queryParams.value, includeRequirements).then((response: PdfShiftsCalendar) => {
    downloadFile(response);
  }).finally(() => {
    pdfDownloadSnackbarAlert.value = false;
  });
}

function downloadShiftsExcel() {
  excelDownloadSnackbarAlert.value = true;
  shiftsApi.indexAsCalendarXlsx(queryParams.value).then((response: XlsxShiftsCalendar) => {
    downloadFile(response);
  }).finally(() => {
    excelDownloadSnackbarAlert.value = false;
  });
}

</script>
<template>
  <v-skeleton-loader
    :loading="skeletonLoaderActive"
    type="table"
    class="w-100"
  >
    <shifts-calendar-filters
      v-model:drawer="drawer"
      v-model:patient-id="filters.patientIdEq"
      v-model:start-date-time-filter="filters.startDateTimeFilter"
      v-model:group-by-class="filters.groupByClass"
      v-model:career-in="filters.caregiverCareerIn"
      v-model:group-ids="filters.patientGroupsIdIn"
      v-model:patient-filter-focused="isPatientFilterFocused"
      v-model:caregiver-filter-focused="isCaregiverFilterFocused"
      :caregiver-id="filters.caregiverIdEq"
      :patients="patientsByCurrentCaregiver"
      :caregivers="caregiversByCurrentPatient"
      :show-group-by-filter="isGeneralViewActive"
      :show-career-filter="!currentCaregiver"
      @update:caregiver-id="onCaregiverSelected"
      @apply="applyAdvancedFilters"
    />
    <shifts-calendar-grid
      ref="gridRef"
      :calendar="shiftsAsCalendar"
      :view-group-by-class="currentViewGroupByClass"
      :loading="isShiftsAsCalendarFetching"
      :show-row-actions="!!currentPatient"
      :block-row-patient-id="blockRowPatientId"
      @shift-click="openShiftShowDialog"
      @create-cell="onCreateCell"
      @create-date="onCreateDate"
      @edit-shifts="onEditShifts"
      @stop-shifts="onStopShifts"
      @create-block-cell="onCreateBlockCell"
      @block-click="onBlockClick"
    >
      <template #toolbar>
        <shifts-calendar-toolbar
          :title="title"
          :date-title="shiftsAsCalendar?.dateTitle"
          :anchor-date="toolbarAnchorDate"
          :loading="isShiftsAsCalendarFetching"
          :pdf-downloading="pdfDownloadSnackbarAlert || isBulkExporting"
          :show-requirements-pdf="showRequirementsPdf"
          :excel-downloading="excelDownloadSnackbarAlert"
          :current-patient="currentPatient"
          :current-caregiver="currentCaregiver"
          :deleted-shifts-date-range="deletedShiftsDateRange"
          @today="resetOffset"
          @previous="updateOffset(-1)"
          @next="updateOffset(1)"
          @date-selected="onToolbarDateSelected"
          @download-pdf="downloadShiftsPDF()"
          @download-pdf-with-requirements="downloadShiftsPDF(true)"
          @download-excel="downloadShiftsExcel"
          @success="actionSuccess = true"
          @refetch="refetchAsCalendar"
        />
      </template>
    </shifts-calendar-grid>

    <bulk-export-snackbars
      :is-exporting="isBulkExporting"
      :progress-label="bulkExportProgressLabel"
      :last-result="bulkExportLastResult"
      :last-error-message="bulkExportLastErrorMessage"
    />

    <shift-show-dialog
      ref="shiftShowDialogRef"
      @success="actionSuccess = true"
      @refetch="refetchAsCalendar"
    />

    <!-- Shared shift create dialog -->
    <responsive-dialog
      v-model="showCreateDialog"
      max-width="800"
      scrollable
    >
      <shift-form
        v-if="showCreateDialog && selectedCreateShift"
        :shift="selectedCreateShift as Shift"
        :date="selectedCreateDate!"
        mode="new"
        :source="selectedCreateSource === 'date' ? 'date' : undefined"
        :show-block-toggle="canOfferScheduleBlock"
        @close="showCreateDialog = false"
        @success="actionSuccess = true"
        @refetch="refetchAsCalendar"
        @switch-to-block="onSwitchToBlock"
      />
    </responsive-dialog>

    <!-- Shared batch update dialog -->
    <responsive-dialog
      v-model="showBatchUpdateDialog"
      max-width="800"
      scrollable
    >
      <shift-batch-update-form
        v-if="showBatchUpdateDialog && currentPatient && selectedRowCaregiver"
        :patient="currentPatient"
        :caregiver="selectedRowCaregiver"
        :caregivers="caregivers"
        @close="showBatchUpdateDialog = false"
        @success="actionSuccess = true"
        @refetch="refetchAsCalendar"
      />
    </responsive-dialog>

    <!-- Shared batch destroy dialog -->
    <responsive-dialog
      v-model="showBatchDestroyDialog"
      max-width="500"
      scrollable
    >
      <shift-batch-destroy-form
        v-if="showBatchDestroyDialog && currentPatient && selectedRowCaregiver"
        :patient="currentPatient"
        :caregiver="selectedRowCaregiver"
        @close="showBatchDestroyDialog = false"
        @success="actionSuccess = true"
        @refetch="refetchAsCalendar"
      />
    </responsive-dialog>

    <!-- Shared patient schedule block dialog -->
    <responsive-dialog
      v-model="showBlockDialog"
      max-width="720"
      scrollable
    >
      <patient-schedule-block-form
        v-if="showBlockDialog"
        :block="selectedBlock ?? undefined"
        :initial="blockPrefill ?? undefined"
        :lock-patient="!!blockPrefill?.patientId"
        :show-shift-toggle="canOfferScheduleBlock && !selectedBlock"
        @close="showBlockDialog = false"
        @success="actionSuccess = true"
        @switch-to-shift="onSwitchToShift"
      />
    </responsive-dialog>

    <v-snackbar
      v-model="actionSuccess"
      :timeout="2000"
      color="success"
      rounded="pill"
    >
      <v-icon
        icon="mdi-check-circle"
        class="mr-4"
      />
      {{ t('shiftsCalendar.successMessage') }}
    </v-snackbar>
    <v-snackbar
      v-model="caregiverWithoutShifts"
      :timeout="4000"
      color="warning"
      rounded="pill"
    >
      <v-icon
        icon="mdi-information-outline"
        class="mr-2"
      />
      {{ t('shiftsCalendar.caregiverWithoutShifts') }}
    </v-snackbar>
    <v-snackbar
      v-model="pdfDownloadSnackbarAlert"
      :timeout="2000"
      color="primary"
      rounded="pill"
    >
      {{ t('shiftsCalendar.generatingPdf') }}
    </v-snackbar>
    <v-snackbar
      v-model="emptyCalendarSnackbarAlert"
      :timeout="4000"
      color="warning"
      rounded="pill"
    >
      {{ t('bulkExports.emptyCalendar') }}
    </v-snackbar>
    <v-snackbar
      v-model="excelDownloadSnackbarAlert"
      :timeout="2000"
      color="primary"
      rounded="pill"
    >
      {{ t('shiftsCalendar.generatingExcel') }}
    </v-snackbar>
    <shifts-list-summary
      v-if="showSummary"
      :summary="shiftsAsCalendar?.summary || []"
      :total-summary="shiftsAsCalendar?.totalSummary ||
        { totalShifts: 0, totalPrice: 0, totalCost: 0, totalProfit: 0, margin: 0 }"
      class="mt-8"
    />
  </v-skeleton-loader>
</template>
